import React, { useState, useRef } from 'react';
import { uploadFile } from '../lib/upload';
import { compressImage } from '../lib/image';
import { BUCKETS } from '../lib/supabase';
import { readFileAsDataURL } from '../lib/cropImage';
import ImageCropper from './ImageCropper';

const parseRatio = (ratioLabel) => {
  if (!ratioLabel || !String(ratioLabel).includes(':')) return null;
  const [w, h] = String(ratioLabel).split(':').map(Number);
  if (!w || !h) return null;
  return w / h;
};

/**
 * Image Upload with an interactive crop window: the user frames exactly the
 * portion of the photo that should be visible before it is uploaded.
 */
export function ImageUpload({
  bucket,
  folder,
  onUpload,
  currentImage,
  label = "Upload Image",
  entityId = null,
  enableProjectionEditor = true,   // legacy prop name, kept for existing callers
  enableCrop = true,
  cropAspect = null,
  cropShape = null,
  maxFileSizeMB = 5,
  enforceAspectRatios = [],
  outputMaxSizeMB = null,
}) {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(currentImage || null);
  const [cropSource, setCropSource] = useState(null);
  const [pendingFile, setPendingFile] = useState(null);
  const [naturalAspect, setNaturalAspect] = useState(1);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const isAvatar = folder === 'avatars' || bucket === BUCKETS.AVATARS;
  const cropEnabled = enableCrop && enableProjectionEditor !== false;

  const enforcedRatios = (enforceAspectRatios || []).map(parseRatio).filter(Boolean);
  const defaultAspect = cropAspect || (isAvatar ? 1 : enforcedRatios[0] || naturalAspect);
  const shape = cropShape || (isAvatar ? 'round' : 'rect');
  const ratioOptions = isAvatar
    ? []
    : (enforceAspectRatios && enforceAspectRatios.length
      ? enforceAspectRatios.map((label) => ({ label, value: parseRatio(label) })).filter((o) => o.value)
      : [
        { label: 'Original', value: naturalAspect },
        { label: 'Square', value: 1 },
        { label: 'Portrait', value: 3 / 4 },
        { label: 'Landscape', value: 4 / 3 },
      ]);

  const uploadProcessedFile = async (file) => {
    setError('');
    setUploading(true);

    try {
      let compressedFile;
      if (bucket === BUCKETS.AVATARS) {
        compressedFile = await compressImage(file, 600, 0.85);
      } else if (bucket === BUCKETS.ARTWORKS || bucket === BUCKETS.ARTIST_ARTWORKS) {
        compressedFile = await compressImage(file, 1600, 0.88);
      } else {
        compressedFile = await compressImage(file, 1200, 0.85);
      }

      let finalFile = compressedFile;
      if (outputMaxSizeMB && finalFile.size > outputMaxSizeMB * 1024 * 1024) {
        let quality = 0.82;
        let maxWidth = 1400;
        for (let i = 0; i < 5 && finalFile.size > outputMaxSizeMB * 1024 * 1024; i += 1) {
          finalFile = await compressImage(finalFile, maxWidth, quality);
          quality = Math.max(0.5, quality - 0.08);
          maxWidth = Math.max(900, Math.floor(maxWidth * 0.9));
        }
      }

      const imageUrl = await uploadFile({
        file: finalFile,
        bucketKey: bucket,
        folder,
        entityId,
      });

      setPreview(imageUrl);
      setCropSource(null);
      setPendingFile(null);
      onUpload(imageUrl, null);
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.message || 'Failed to upload image');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    if (file.size > maxFileSizeMB * 1024 * 1024) {
      setError(`Image size should be less than ${maxFileSizeMB}MB`);
      return;
    }

    setError('');

    if (!cropEnabled) {
      await uploadProcessedFile(file);
      return;
    }

    const dataUrl = await readFileAsDataURL(file);
    const measured = await new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(img.naturalWidth / img.naturalHeight || 1);
      img.onerror = () => resolve(1);
      img.src = dataUrl;
    });
    setNaturalAspect(measured);
    setPendingFile(file);
    setCropSource(dataUrl);
  };

  const cancelCrop = () => {
    setCropSource(null);
    setPendingFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className={isAvatar ? 'flex flex-col items-center text-center space-y-3' : 'space-y-3'}>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
        data-testid="image-upload-input"
      />

      {preview && !cropSource && (
        <div className="relative inline-block">
          <img
            src={preview}
            alt="Preview"
            className={`object-cover shadow-md ${
              isAvatar ? 'w-32 h-32 rounded-full' : 'max-w-sm max-h-64 rounded-lg'
            }`}
          />
          {!uploading && (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="absolute top-2 right-2 bg-white rounded-full px-3 py-1 text-xs font-medium shadow-lg hover:bg-gray-100"
              title="Change / re-crop image"
              data-testid="image-change-button"
            >
              Change
            </button>
          )}
        </div>
      )}

      {!preview && (
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="image-upload-button"
        >
          {uploading ? 'Uploading...' : label}
        </button>
      )}

      {cropSource && (
        <ImageCropper
          imageSrc={cropSource}
          fileName={pendingFile?.name || 'image.jpg'}
          aspect={defaultAspect}
          cropShape={shape}
          allowRatioChange={!isAvatar && ratioOptions.length > 1}
          ratioOptions={ratioOptions}
          busy={uploading}
          onCancel={cancelCrop}
          onCropped={uploadProcessedFile}
          title={isAvatar ? 'Crop your photo' : 'Crop your image'}
        />
      )}

      {error && <p className="text-sm text-red-600" data-testid="image-upload-error">{error}</p>}

      {uploading && (
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <div className="animate-spin h-4 w-4 border-2 border-orange-500 border-t-transparent rounded-full"></div>
          <span>Optimizing and uploading...</span>
        </div>
      )}

      <p className="text-xs text-gray-500">
        Supported: JPG, PNG, WebP (Max {maxFileSizeMB}MB). You can drag and zoom to choose the visible area.
      </p>
    </div>
  );
}

export default ImageUpload;
